// Local Font Preloader Utility
// Prevents FOUT (Flash of Unstyled Text) by preloading local fonts at app startup

const LOCAL_FONTS = [
  'Roboto',
  'Inter',
  'Kirsty',
  'Komika Hand',
  'Liberation Sans',
  'Orbitron',
  'SF Cartoonist Hand'
];

class LocalFontPreloader {
  constructor() {
    this.loadedFonts = new Set();
    this.loadingPromise = null;
  }

  async loadFont(fontName) {
    if (this.loadedFonts.has(fontName)) {
      return;
    }

    try {
      // Use CSS Font Loading API to preload the font
      if (document.fonts && document.fonts.load) {
        try {
          await document.fonts.load(`400 16px "${fontName}"`);
          this.loadedFonts.add(fontName);
        } catch (fontError) {
          return fontError;
        }
      } else {
        // Fallback for browsers without CSS Font Loading API
        this.loadedFonts.add(fontName);
      }
    } catch (error) {
      return error;
    }
  }

  async preloadAllFonts() {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = Promise.all(
      LOCAL_FONTS.map(fontName => this.loadFont(fontName))
    ).then(() => {
      return LOCAL_FONTS;
    }).catch(() => {
      return LOCAL_FONTS; // Return fonts list even if some failed
    }).finally(() => {
      this.loadingPromise = null; // Reset for potential retries
    });

    return this.loadingPromise;
  }

  async waitForFontsReady() {
    // If fonts are already being loaded, wait for that promise
    if (this.loadingPromise) {
      await this.loadingPromise;
    } else {
      // If no loading is in progress, start loading
      await this.preloadAllFonts();
    }
    
    // Additional check: verify fonts are actually loaded using document.fonts.check
    if (document.fonts && document.fonts.check) {
      for (const fontName of LOCAL_FONTS) {
        const fontString = `400 16px "${fontName}"`;
        if (!document.fonts.check(fontString)) {
          // Font not loaded, try to load it again
          try {
            await document.fonts.load(fontString);
          } catch (error) {
            // Silent error handling
          }
        }
      }
    }
  }

  getLoadedFonts() {
    return Array.from(this.loadedFonts);
  }

  isFontLoaded(fontName) {
    return this.loadedFonts.has(fontName);
  }
}

// Create singleton instance
const localFontPreloader = new LocalFontPreloader();

export default localFontPreloader;
export { LOCAL_FONTS };
